import type { IListPageFilterConfig, IListPageProps } from './list-page.types';

const NO_ACTIVE_FILTERS = 0;

/**
 * Filters whose `appliedFilters` slot holds a single string value (`'single'` and
 * `'date-range'`) versus the default `'multi'` shape that holds a `string[]`.
 */
export const isStringValuedFilter = (type: IListPageFilterConfig['type']): boolean =>
	type === 'single' || type === 'date-range';

/**
 * Counts how many filter slots in `values` are non-empty according to each config's `type`.
 * Drives the badge on the filter trigger button.
 */
export const countActiveFilters = (
	configs: IListPageFilterConfig[],
	values: Record<string, unknown>
): number => {
	let count = NO_ACTIVE_FILTERS;
	configs.forEach(config => {
		const value = values[config.paramName];
		if (isStringValuedFilter(config.type)) {
			if (typeof value === 'string' && value.length > 0) count += 1;
		} else if (Array.isArray(value) && value.length > 0) {
			count += 1;
		}
	});
	return count;
};

/**
 * Builds the "all filters cleared" shape: `''` for string-valued slots, `[]` for multi.
 * Used as the seed value for `appliedFilters` and as the reset target for the dialog's
 * Clear button.
 */
export const buildEmptyFilters = (configs: IListPageFilterConfig[]): Record<string, unknown> => {
	const empty: Record<string, unknown> = {};
	configs.forEach(config => {
		empty[config.paramName] = isStringValuedFilter(config.type) ? '' : [];
	});
	return empty;
};

/**
 * For each `type: 'date-range'` filter, replaces the selected option's string value with
 * the resolved `{ startDate, endDate }` payload from `option.range()`. Called at fetch
 * time so "now-relative" ranges anchor to the moment the API request fires. Non-date-range
 * filters pass through unchanged.
 */
export const resolveDateRangeFilters = (
	filters: Record<string, unknown> | undefined,
	configs: IListPageFilterConfig[]
): Record<string, unknown> | undefined => {
	if (!filters || configs.length === 0) return filters;
	const next: Record<string, unknown> = { ...filters };
	configs.forEach(config => {
		if (config.type !== 'date-range') return;
		const selectedValue = filters[config.paramName];
		if (typeof selectedValue !== 'string' || selectedValue.length === 0) {
			next[config.paramName] = null;
			return;
		}
		const option = config.options.find(o => o.value === selectedValue);
		next[config.paramName] = option?.range?.() ?? null;
	});
	return next;
};

/**
 * Discriminates between the API-backed and hook-backed `<ListPage>` prop shapes.
 * Owning a single helper keeps the render path and the unit tests in agreement on
 * what "API-backed" means.
 */
export const isApiBackedProps = <TItem, TRow>(
	props: IListPageProps<TItem, TRow>
): props is Extract<IListPageProps<TItem, TRow>, { apiMethod: unknown }> =>
	'apiMethod' in props && Boolean(props.apiMethod);
